import React, { useState } from "react";
import { Network, MessageSquare, Mail, Linkedin, Calendar, Copy, Check } from "lucide-react";

export default function NetworkAccessPage() {
  return (
    <div data-testid="view-network" className="max-w-5xl">
      <Hero />

      <Section eyebrow="Principe" title="Les contacts sans contexte ne valent rien" index="01">
        <p className="italic text-[#D4AF37] text-lg border-l-2 border-[#D4AF37] pl-5">
          Le contexte sans contacts ne va nulle part.
        </p>
      </Section>

      <Section eyebrow="Approche" title="Approcher un fournisseur sans se faire ignorer" index="02">
        <p>
          La plupart des approches échouent pour une raison simple : elles sont centrées sur ce que <em>tu</em>
          veux, pas sur ce que <em>lui</em> voit comme valeur.
        </p>
        <p className="italic text-[#D4AF37]">
          Principe fondamental : sois une solution avant d'être une demande.
        </p>

        <h4 className="font-serif text-xl mt-8 text-white">Avant le contact, réponds à 3 questions</h4>
        <ol className="mt-5 space-y-3 list-decimal pl-6 text-sm">
          <li>Qu'est-ce que ce fournisseur cherche en ce moment ? (Un marché, un acheteur, une simplification logistique ?)</li>
          <li>Qu'est-ce que tu peux lui apporter que ses contacts actuels n'apportent pas ?</li>
          <li>Peux-tu prouver que tu as un acheteur sérieux, ou que tu représentes une demande réelle ?</li>
        </ol>
        <p>Si tu ne peux pas répondre : tu n'es pas prêt à approcher ce fournisseur.</p>

        <h4 className="font-serif text-xl mt-10 text-white">Le processus en 3 temps</h4>
        <div className="mt-6 space-y-4">
          {[
            { t: "01", name: "Légitimation", body: "Avant le contact direct, existe là où il peut te vérifier : profil LinkedIn complet, référence d'un contact commun, article/publication qui te positionne. Quand il reçoit ton message, il doit pouvoir te crédibiliser en 60 secondes." },
            { t: "02", name: "Premier contact", body: "Court. Clair. Spécifique. Pas de « je suis très intéressé par vos produits ». Plutôt : « Je représente un acheteur [secteur, pays] qui recherche [produit, spec, volume] pour livraison [trimestre]. Avez-vous de la disponibilité, à qui dois-je m'adresser ? »" },
            { t: "03", name: "Suivi", body: "La plupart abandonnent après 1-2 messages sans réponse. Les pros qui réussissent sont ceux qui suivent avec régularité et valeur ajoutée — pas de harcèlement, une présence constante et utile." },
          ].map((s) => (
            <div key={s.t} className="flex gap-6 border border-white/10 bg-[#0c0c0c] p-6">
              <div className="font-serif text-2xl text-[#D4AF37] shrink-0">{s.t}</div>
              <div>
                <div className="font-serif text-lg text-white">{s.name}</div>
                <p className="text-sm text-[#A1A1AA] mt-2">{s.body}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section eyebrow="Scripts" title="Messages prêts à copier (Email, LinkedIn, WhatsApp)" index="03">
        <p>
          Trois scripts de référence — à personnaliser, jamais à copier-coller bêtement. Le signal de sérieux : un
          message factuel, sans superlatif, avec une action claire.
        </p>

        <ScriptCard
          icon={Mail}
          label="Email"
          title="Premier contact fournisseur"
          body={`Objet : Acheteur qualifié — [Produit] — [Volume] — [Trimestre]

Bonjour [Nom],

Je me permets de vous contacter directement car je travaille actuellement avec un acheteur basé en [Pays] qui recherche [Produit, Spec] pour un volume de [X MT/mois] en livraison [CIF/FOB] [Port] à partir de [Date].

L'acheteur est une entité enregistrée, capable d'émettre une LC via [Banque ou type de banque]. Nous avons déjà finalisé des transactions sur des produits similaires.

Seriez-vous disponible pour un bref échange cette semaine afin d'évaluer si vos disponibilités correspondent à notre besoin ?

Cordialement,
[Nom] | [Titre] | [Entité]`}
        />

        <ScriptCard
          icon={Linkedin}
          label="LinkedIn"
          title="Connexion + message initial"
          body={`Bonjour [Prénom],

Je suis actif dans le trading de [Produit] sur les marchés [Europe/Afrique/etc.] et je travaille régulièrement avec des acheteurs institutionnels. Votre profil m'indique que vous êtes du côté offre sur ce secteur.

Je souhaite vous ajouter à mon réseau — je pourrais avoir une demande concrète à vous soumettre prochainement.

Bien à vous,
[Nom]`}
          footer="Ne mets jamais de deal dans un premier message LinkedIn. La connexion d'abord, la conversation ensuite."
        />

        <ScriptCard
          icon={MessageSquare}
          label="WhatsApp"
          title="Suivi après échange initial"
          body={`Bonjour [Prénom], bonne semaine.

Je reviens vers vous concernant notre échange sur [Produit]. Mon acheteur reste actif sur ce besoin. Avez-vous eu l'occasion d'évaluer la disponibilité de votre côté ?

Si ça peut aider, je peux vous envoyer un LOI formel dès cette semaine pour confirmation de votre intérêt.

Merci,
[Nom]`}
        />
      </Section>

      <Section eyebrow="Plan 30 jours" title="Construire un réseau crédible en 30 jours" index="04">
        <p>
          Ce n'est pas un réseau de 500 contacts. C'est un réseau de
          <strong className="text-white"> 20 personnes</strong> qui savent qui tu es, ce que tu fais, et pourquoi tu
          es sérieux.
        </p>

        <div className="mt-10 space-y-6">
          {[
            {
              week: "Semaine 01",
              title: "Positionnement & présence",
              items: [
                "Crée/refais complètement ton profil LinkedIn : titre clair (ex : « Commodity Trade Facilitator | Crude Oil & Agri | Africa-Europe Corridor »), résumé qui montre ta valeur — pas ton CV.",
                "Identifie 5 secteurs ou géographies sur lesquels tu vas te spécialiser. Ne dis pas « je fais tout ».",
                "Partage du contenu : analyse de marché, commentaire sur l'actualité des prix, observation sur un trade corridor. Montre que tu sais de quoi tu parles.",
              ],
            },
            {
              week: "Semaine 02",
              title: "Connexions ciblées",
              items: [
                "Connecte-toi avec 30 profils ultra-ciblés : traders, heads of procurement, commodity bankers, compliance officers de trading houses.",
                "Pour chaque connexion acceptée : message court, non-commercial (« Merci pour la connexion. Je suis actif sur [sujet], n'hésitez pas si un échange peut avoir de la valeur »).",
                "Rejoins 3 à 5 groupes LinkedIn spécialisés. Commente intelligemment — pas « great post! », des observations substantielles.",
              ],
            },
            {
              week: "Semaine 03",
              title: "Conversations réelles",
              items: [
                "Identifie 5 à 10 personnes avec qui une conversation a du sens. Propose un appel de 20 min — pas un pitch, une conversation professionnelle.",
                "Pendant ces appels : écoute plus que tu ne parles. Comprends problèmes, besoins, frustrations. Note tout.",
                "Après chaque appel : identifie une façon d'apporter de la valeur sans rien demander en retour (information, introduction, article pertinent).",
              ],
            },
            {
              week: "Semaine 04",
              title: "Consolidation & activation",
              items: [
                "Bilan des 20 contacts les plus pertinents. Classe-les : seller-side, buyer-side, banque/financement, legal/compliance.",
                "Pour chaque catégorie, assure-toi d'avoir au moins 3 contacts actifs.",
                "Commence à faire des introductions croisées dans ton réseau. C'est le geste qui te positionne comme connecteur central — pas quelqu'un qui cherche, quelqu'un qui crée de la valeur.",
              ],
            },
          ].map((w, i) => (
            <div key={w.week} className="border border-white/10 bg-[#0c0c0c] p-6 md:p-8">
              <div className="flex items-baseline justify-between mb-4">
                <div className="flex items-baseline gap-4">
                  <span className="font-serif text-xl text-[#D4AF37]">{w.week}</span>
                  <span className="font-serif text-xl text-white">— {w.title}</span>
                </div>
                <Calendar className="h-4 w-4 text-[#A1A1AA]" />
              </div>
              <ul className="space-y-3 text-sm text-[#E4E4E7]">
                {w.items.map((item, j) => (
                  <li key={j} className="flex gap-3">
                    <span className="text-[#D4AF37] shrink-0 font-mono text-xs mt-0.5">{String(j + 1).padStart(2, "0")}</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <Callout icon={Network} title="Le principe long terme">
          Un réseau se construit par la <strong className="text-white">générosité et la constance</strong>, pas par
          l'urgence et l'intérêt immédiat. Chaque personne dans ton réseau doit sentir que te connaître lui apporte
          quelque chose. Le jour où tu auras besoin d'eux — pour un deal, une vérification, une introduction — ils
          répondront parce que tu auras été présent <em>avant</em> d'avoir besoin.
        </Callout>
      </Section>
    </div>
  );
}

// ---------- Subcomponents ----------
const Hero = () => (
  <section className="relative mb-16 border border-white/10 bg-[#0c0c0c] overflow-hidden">
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,rgba(212,175,55,0.12),transparent_60%)]" />
    <div className="relative p-10 md:p-14">
      <div className="flex items-center gap-3">
        <Network className="h-5 w-5 text-[#D4AF37]" />
        <span className="text-[11px] uppercase tracking-[0.4em] text-[#D4AF37]">Stratégie · 03</span>
      </div>
      <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-tight mt-6 max-w-3xl">
        <em className="italic text-[#F5F5DC]">Accès</em> &amp; <span className="text-[#D4AF37]">Réseau</span>.
      </h1>
      <p className="mt-6 max-w-2xl text-[#A1A1AA] leading-relaxed">
        Comment approcher un fournisseur sans être ignoré, scripts prêts à l'emploi pour Email/LinkedIn/WhatsApp,
        et un plan 30 jours pour construire un réseau crédible de 20 contacts réellement utiles.
      </p>
    </div>
  </section>
);

const Section = ({ eyebrow, title, index, children }) => (
  <section className="mb-20">
    <div className="flex items-baseline gap-6 mb-8">
      <span className="font-serif text-xl text-[#D4AF37] tabular-nums">{index}</span>
      <div>
        <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]">{eyebrow}</div>
        <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl font-light mt-2 leading-tight">{title}</h2>
      </div>
    </div>
    <div className="text-[#C4C4C7] leading-relaxed space-y-4 pl-0 md:pl-10">{children}</div>
  </section>
);

const Callout = ({ icon: Icon, title, children }) => (
  <div className="mt-10 border-l-2 border-[#D4AF37] bg-[#141414] p-6 md:p-8">
    <div className="flex items-center gap-3">
      <Icon className="h-4 w-4 text-[#D4AF37]" />
      <span className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">{title}</span>
    </div>
    <div className="mt-4 text-[#E4E4E7] leading-relaxed text-sm">{children}</div>
  </div>
);

const ScriptCard = ({ icon: Icon, label, title, body, footer }) => {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(body);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };
  return (
    <div className="mt-8 border border-white/10 bg-[#0c0c0c] overflow-hidden">
      <div className="px-6 py-4 bg-[#141414] border-b border-white/10 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Icon className="h-4 w-4 text-[#D4AF37]" />
          <span className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">{label}</span>
          <span className="font-serif text-base text-white">— {title}</span>
        </div>
        <button
          onClick={onCopy}
          data-testid={`copy-script-${label.toLowerCase()}`}
          className="inline-flex items-center gap-2 px-3 py-1.5 border border-white/15 text-[10px] uppercase tracking-[0.22em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
        >
          {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
          {copied ? "Copié" : "Copier"}
        </button>
      </div>
      <pre className="p-6 md:p-8 whitespace-pre-wrap font-mono text-xs md:text-[13px] text-[#E4E4E7] leading-relaxed">{body}</pre>
      {footer && (
        <div className="px-6 py-3 bg-[#141414] border-t border-white/10 text-xs text-[#A1A1AA] italic">
          Note : {footer}
        </div>
      )}
    </div>
  );
};
